// @givmo/board — the in-meeting record: attendance, sessions, motions, and the quorum figures.
//
// ══ THE LAW THIS MODULE IS SHAPED AROUND ═══════════════════════════════════════════════════════
//
// **A RECUSED DIRECTOR STILL COUNTS TOWARD QUORUM** (DGCL § 144(d)(1); Board Governance plan §3.7
// records auto-dropping them as a NAMED BUG CLASS — a board that lost quorum every time a director
// was conflicted could not act at all).
//
// The backend defends that structurally: recusal lives in its own table so nothing counting votes can
// subtract it, and `app/domain/board_quorum.py` has NO ARGUMENT ANYWHERE through which a recusal could
// reach the arithmetic. This module's contribution to the same law is smaller and absolute:
//
//   ⚠ **THERE IS NO QUORUM ARITHMETIC IN THIS FILE, AND THERE MUST NEVER BE ANY.**
//
// `seats_counted`, `seats_present`, `required_seats` and `shortfall_seats` are CAPTURED ON THE MOTION
// at the moment of the vote and arrive on the wire already computed. This module renders them
// verbatim. It does not subtract recusals, it does not subtract anything, it does not recompute a
// shortfall, and it does not derive a presence count from the attendance rows — every one of those
// would be a second, drifting implementation of the rule the backend went to that much trouble to make
// unreachable, and the first of them is the exact bug §3.7 names.
//
// The counts are captured rather than re-derived for plan §4's reason, which binds the read side too:
// correcting an attendance row, or recording a new quorum parameter next year, must not restate what a
// past vote's record says about the room.
//
// ══ AND THE CONCLUSION IS NEVER OURS TO STATE ══════════════════════════════════════════════════
//
// §3.4 bans "quorum met" and "properly noticed" as product voice, and the terminology rail enforces
// it over this app's copy AND its identifier names. There is no field on the wire meaning "quorum was
// met" — deliberately — and there is none here. What the surface shows is arithmetic plus the
// organization's own recorded parameter with its bylaw attribution, in that shape:
//
//     "6 of 9 voting directors present · your recorded quorum: majority (Art. III §4)"
//
// A shortfall is a NUMBER OF SEATS, flagged, and it never blocks anything. A FRACTION basis yields no
// required seat count at all — turning "two-thirds of the directors" into a number of seats needs a
// rounding convention and instruments differ, so the backend echoes the fraction exactly as the
// organization wrote it and computes no shortfall. This module renders that absence AS an absence; it does
// not fill it in.
//
// Likewise `outcome`: whether a motion carried is the chair's declaration, recorded by the officer.
// Nothing here computes it from a tally, and a tally that disagrees with the recorded outcome is
// rendered as what it is — both are the organization's record.

import { extractList } from '@givmo/api-client/list';
import type {
  BoardLapsedTermFlagOut,
  BoardMeetingAttendanceOut,
  BoardMeetingSegmentOut,
  BoardMeetingSegmentParticipantOut,
  BoardMotionOut,
  BoardMotionRecusalOut,
  BoardMotionVoteOut,
  BoardQuorumOut,
  BoardSegmentStepOutOut,
  OrgBoardMemberOut,
} from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';
import { humanize } from '../../lib/display';
import { instantToZonedFields, isUsableTimeZone } from '../meetings/zoned-time';
import { isDirector, servingNonDirectors } from '../roster/officer-roles';
import { isHistoricalSeat } from '../roster/roster-decode';

export { displayDate, displayInstant, todayAsDateInput } from '../../lib/display';

type BadgeTone = NonNullable<BadgeProps['tone']>;

// ── The quorum figures ───────────────────────────────────────────────────────────────────────────

/**
 * The presence sentence, built ONLY from figures the backend captured.
 *
 * Returns `null` when the wire carried no counts — a motion recorded without a vote has no
 * quorum-at-vote, and an absent figure is rendered as absent rather than as a zero. A zero would read
 * as "nobody was there", which is a statement about the room that nothing recorded.
 *
 * ⚠ Read the header before touching this. Both numbers come off the wire; neither is derived from the
 * attendance list, and NOTHING is subtracted from either.
 */
export function presenceSentence(quorum: BoardQuorumOut | null | undefined): string | null {
  const present = quorum?.seats_present;
  const counted = quorum?.seats_counted;
  if (typeof present !== 'number' || typeof counted !== 'number') return null;
  const noun = counted === 1 ? 'voting director' : 'voting directors';
  return `${present} of ${counted} ${noun} present`;
}

/**
 * The organization's own recorded quorum parameter, with the section it attributed it to.
 *
 * The citation is the organization's text and is rendered verbatim — never parsed, never validated, never
 * turned into a claim about what the instrument requires (§3.4's `your-bylaws-require` ban). Returns
 * `null` when the organization has recorded no parameter, which is a real and common state: the module
 * still counts the room, it just has nothing to count it against.
 */
export function recordedQuorumSentence(quorum: BoardQuorumOut | null | undefined): string | null {
  const summary = (quorum?.parameter_summary ?? '').trim();
  if (summary.length === 0) return null;
  const citation = (quorum?.bylaw_citation ?? '').trim();
  return citation.length > 0
    ? `your recorded quorum: ${summary} (${citation})`
    : `your recorded quorum: ${summary}`;
}

/**
 * The shortfall, as a number of seats, when the backend computed one.
 *
 * ⚠ ABSENT FOR A FRACTION BASIS, and that absence is load-bearing rather than a gap. "Two-thirds of
 * the directors" has no seat count without a rounding convention, instruments differ about which one,
 * and adjudicating between them is a governance judgment the module does not make. So the backend
 * supplies no `required_seats` for a fraction and this returns `null` — the surface shows the recorded
 * parameter and the count, and says nothing about the difference between them.
 */
export function shortfallSeats(quorum: BoardQuorumOut | null | undefined): number | null {
  const shortfall = quorum?.shortfall_seats;
  return typeof shortfall === 'number' && shortfall > 0 ? shortfall : null;
}

/** Whether the backend flagged the captured count as short of the recorded parameter. */
export function isShortOfRecordedQuorum(quorum: BoardQuorumOut | null | undefined): boolean {
  return quorum?.is_short_of_recorded_quorum === true;
}

/**
 * Whether there is anything at all to render about quorum for this motion.
 *
 * The backend's own rendered `statement` is preferred wherever it is present (the notice block's
 * precedent: the sentence the backend wrote is the sentence shown), so this admits a quorum object
 * carrying only that.
 */
export function hasQuorumFigures(quorum: BoardQuorumOut | null | undefined): boolean {
  if (quorum === null || quorum === undefined) return false;
  return (
    presenceSentence(quorum) !== null ||
    recordedQuorumSentence(quorum) !== null ||
    (quorum.statement ?? '').trim().length > 0
  );
}

// ── Attendance ───────────────────────────────────────────────────────────────────────────────────

export function extractAttendance(body: unknown): BoardMeetingAttendanceOut[] {
  return extractList<BoardMeetingAttendanceOut>(body, 'data');
}

/** Attendance by seat id, so a roster row can find its own record in one lookup. */
export function attendanceBySeat(
  rows: readonly BoardMeetingAttendanceOut[],
): Map<string, BoardMeetingAttendanceOut> {
  const index = new Map<string, BoardMeetingAttendanceOut>();
  for (const row of rows) index.set(row.board_member_id, row);
  return index;
}

/**
 * How a seat's presence reads.
 *
 * ⚠ `arrived_at` / `departed_at` are optional INSTANTS, NOT STATES, and the label keeps them that
 * way. "Arrived late" and "left early" are both true of one seat that did both, which four
 * mutually-exclusive labels cannot express — so the times are rendered beside the presence value
 * rather than folded into it.
 */
export function attendanceLabel(value: string | null | undefined): string {
  switch (value) {
    case 'present':
      return 'Present';
    case 'absent':
      return 'Absent';
    default:
      return humanize(value);
  }
}

export function attendanceTone(value: string | null | undefined): BadgeTone {
  switch (value) {
    case 'present':
      return 'success';
    case 'absent':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/** The RSVP's label — what a director SAID they would do, the opposite number of attendance. */
export function intentLabel(value: string | null | undefined): string {
  switch (value) {
    case 'attending':
      return 'Said they would attend';
    case 'not_attending':
      return 'Said they would not';
    case 'tentative':
      return 'Said tentatively';
    default:
      return humanize(value);
  }
}

// ── Sessions (segments) ──────────────────────────────────────────────────────────────────────────

export function extractSegments(body: unknown): BoardMeetingSegmentOut[] {
  return extractList<BoardMeetingSegmentOut>(body, 'data');
}

/**
 * Whether a segment is an open session.
 *
 * ⚠ STATED POSITIVELY, mirroring `app/domain/board_minutes.py:may_read_segment`, so that a segment
 * type a later backend item adds fails CLOSED here exactly as it does there. `!== 'executive_session'`
 * would open every future type by default.
 */
export function isOpenSession(segment: BoardMeetingSegmentOut | null | undefined): boolean {
  return segment?.segment_type === 'open_session';
}

export function segmentTypeLabel(value: string | null | undefined): string {
  switch (value) {
    case 'open_session':
      return 'Open session';
    case 'executive_session':
      return 'Executive session';
    default:
      return humanize(value);
  }
}

export function segmentTypeTone(value: string | null | undefined): BadgeTone {
  return value === 'executive_session' ? 'warning' : 'neutral';
}

/**
 * A segment's title for reading, falling back to its type.
 *
 * The organization may leave a session untitled; showing the type is better than showing an empty heading,
 * and it never invents a name.
 *
 * ⚠ A CLOSED SESSION SAYS SO EVEN WHEN IT HAS A TITLE. An untitled one fell back to "Executive
 * session" and read correctly; a titled one returned the bare title, so "Personnel" was
 * indistinguishable from an open session in every picker and list that uses this — including the
 * minutes scope picker, where choosing wrong means filing minutes at the wrong confidentiality. The
 * type is APPENDED rather than substituted, because the organization's own name for the session is what
 * they are looking for and the type is the qualifier.
 *
 * Only the closed type is qualified. Marking open sessions "(open session)" too would make the
 * distinction a pair of labels to compare rather than one thing standing out, which is the read this
 * exists to produce. And a title that ALREADY says it — an organization that names its session "Executive
 * session" — is left alone rather than stuttered into "Executive session (executive session)".
 */
export function segmentHeading(segment: BoardMeetingSegmentOut): string {
  const title = (segment.title ?? '').trim();
  const type = segmentTypeLabel(segment.segment_type);
  if (title.length === 0) return type;
  if (isOpenSession(segment)) return title;
  return title.toLowerCase().includes(type.toLowerCase())
    ? title
    : `${title} (${type.toLowerCase()})`;
}

export function extractParticipants(
  segment: BoardMeetingSegmentOut,
): BoardMeetingSegmentParticipantOut[] {
  return [...(segment.participants ?? [])];
}

/**
 * CBG-RC12-REC — who stepped out of a closed session for a conflict of interest, as recorded: the
 * name captured when it was recorded, and the board's note. A backend without the field reads as
 * nobody.
 */
export function extractSteppedOut(segment: BoardMeetingSegmentOut): BoardSegmentStepOutOut[] {
  return [...(segment.stepped_out ?? [])];
}

// ── Motions ──────────────────────────────────────────────────────────────────────────────────────

export function extractMotions(body: unknown): BoardMotionOut[] {
  return extractList<BoardMotionOut>(body, 'data');
}

export function motionStatusLabel(value: string | null | undefined): string {
  switch (value) {
    case 'open':
      return 'Open';
    case 'withdrawn':
      return 'Withdrawn';
    case 'voted':
      return 'Voted';
    default:
      return humanize(value);
  }
}

export function motionStatusTone(value: string | null | undefined): BadgeTone {
  switch (value) {
    case 'voted':
      return 'success';
    case 'withdrawn':
      return 'neutral';
    case 'open':
      return 'info';
    default:
      return 'neutral';
  }
}

/**
 * The recorded outcome.
 *
 * ⚠ THE ORGANIZATION'S RECORDED VALUE, NEVER COMPUTED. Whether a motion carried is the chair's
 * declaration; the module holds the recorded threshold and can state the tally beside it, but §3.2's
 * Band 3 refuses any product-voice statement that an action is valid or effective. Nothing here reads
 * a tally and concludes an outcome.
 */
export function motionOutcomeLabel(value: string | null | undefined): string | null {
  switch (value) {
    case 'carried':
      return 'Recorded as carried';
    case 'failed':
      return 'Recorded as failed';
    case null:
    case undefined:
    case '':
      return null;
    default:
      return humanize(value);
  }
}

export function motionOutcomeTone(value: string | null | undefined): BadgeTone {
  return value === 'carried' ? 'success' : value === 'failed' ? 'danger' : 'neutral';
}

/** A motion whose wording can still be changed — the wording freezes at the VOTE, not at approval. */
export function isWordingFrozen(motion: BoardMotionOut): boolean {
  return motion.status === 'voted';
}

/**
 * CBG-RC12-REC — whether this motion was made in an executive session, the only kind of decision the
 * board can keep out of the meeting's minutes. Stated POSITIVELY on the session's kind, as
 * `isOpenSession` is the other way round: no session, or one this reader isn't shown, is not one.
 */
export function madeInAClosedSession(
  motion: BoardMotionOut,
  segments: readonly BoardMeetingSegmentOut[],
): boolean {
  if (!motion.segment_id) return false;
  const segment = segments.find((row) => row.id === motion.segment_id);
  return segment?.segment_type === 'executive_session';
}

/**
 * CBG-RC12-REC — whether this motion carries the "Keep this decision confidential" switch: a VOTED
 * motion made in an executive session, as frame `r2-decision` draws it. An open or withdrawn motion
 * is no decision (the backend's `decisions_reported` reports voted motions only), so it carries no
 * switch, and no line saying the meeting's minutes report it (review-109's F2).
 */
export function takesTheConfidentialSwitch(
  motion: BoardMotionOut,
  segments: readonly BoardMeetingSegmentOut[],
): boolean {
  return motion.status === 'voted' && madeInAClosedSession(motion, segments);
}

/** CBG-RC12-REC — whether the board kept this decision out of the meeting's minutes. */
export function isKeptConfidential(motion: BoardMotionOut): boolean {
  return motion.kept_confidential === true;
}

/** A motion that approves a set of minutes. Its vote rides the Owner-only route, not the ordinary one. */
export function approvesMinutes(motion: BoardMotionOut): boolean {
  return (motion.approves_minutes_record_id ?? '').trim().length > 0;
}

export function extractCasts(motion: BoardMotionOut): BoardMotionVoteOut[] {
  return [...(motion.casts ?? [])];
}

export function extractRecusals(motion: BoardMotionOut): BoardMotionRecusalOut[] {
  return [...(motion.recusals ?? [])];
}

export function extractLapsedTermFlags(motion: BoardMotionOut): BoardLapsedTermFlagOut[] {
  return [...(motion.lapsed_term_flags ?? [])];
}

/** Seat ids that recused on this motion — for LABELLING a roster row, never for arithmetic. */
export function recusedSeatIds(motion: BoardMotionOut): Set<string> {
  return new Set(extractRecusals(motion).map((entry) => entry.board_member_id));
}

export function voteCastLabel(value: string | null | undefined): string {
  switch (value) {
    case 'for':
      return 'For';
    case 'against':
      return 'Against';
    case 'abstain':
      return 'Abstained';
    default:
      return humanize(value);
  }
}

export function voteCastTone(value: string | null | undefined): BadgeTone {
  switch (value) {
    case 'for':
      return 'success';
    case 'against':
      return 'danger';
    case 'abstain':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/**
 * The recorded tally as a sentence, or `null` when nothing was recorded.
 *
 * Rendered from the tally fields the backend serves. With per-seat casts supplied the backend DERIVES
 * the tally from them rather than accepting one alongside, so these figures and the cast list cannot
 * disagree — that is the backend's invariant, not this module's, and this module does not check it.
 */
export function tallySentence(motion: BoardMotionOut): string | null {
  const forVotes = motion.votes_for;
  const against = motion.votes_against;
  const abstaining = motion.votes_abstaining;
  if (
    typeof forVotes !== 'number' &&
    typeof against !== 'number' &&
    typeof abstaining !== 'number'
  ) {
    return null;
  }
  return `${forVotes ?? 0} for · ${against ?? 0} against · ${abstaining ?? 0} abstaining`;
}

/**
 * The disinterested-director figures, when the board recorded a determination.
 *
 * ⚠ **THE BOARD'S OWN DETERMINATION, NEVER COMPUTED.** Who was disinterested for a conflicted act is
 * something the board decided and stated a basis for; there is no conflict data anywhere in the module
 * from which it could be inferred, and there must never be. This renders what was entered. The
 * backend holds the limbs to all-or-none by CHECK, so a partial set cannot arrive.
 */
export function disinterestedSentence(motion: BoardMotionOut): string | null {
  const forVotes = motion.disinterested_votes_for;
  const against = motion.disinterested_votes_against;
  const abstaining = motion.disinterested_votes_abstaining;
  if (
    typeof forVotes !== 'number' &&
    typeof against !== 'number' &&
    typeof abstaining !== 'number'
  ) {
    return null;
  }
  return `${forVotes ?? 0} for · ${against ?? 0} against · ${abstaining ?? 0} abstaining`;
}

// ── Roster naming ────────────────────────────────────────────────────────────────────────────────

/**
 * Seat id → the organization's recorded display name.
 *
 * ⚠ An attendance row, a cast and a recusal all carry a SOFT seat reference (no foreign key), because
 * they are the evidence of who was in the room and how they voted and that must outlive the roster
 * row. So a name may genuinely be unresolvable — a director removed from the roster after the meeting
 * they attended. That renders as the seat's own identifier rather than as blank or as "Unknown", which
 * would read as a load failure on a screen whose whole job is showing what was recorded.
 */
export function seatNames(roster: readonly OrgBoardMemberOut[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const seat of roster) {
    const name = (seat.display_name ?? '').trim();
    if (name.length > 0) names.set(seat.id, name);
  }
  return names;
}

export function seatName(names: Map<string, string>, seatId: string | null | undefined): string {
  if (seatId === null || seatId === undefined || seatId.length === 0) return '—';
  return names.get(seatId) ?? seatId;
}

// ── Which seats a meeting offers (CBG-FIX41) ─────────────────────────────────────────────────────

/** The calendar day a meeting was held, in the meeting's own zone, and that zone. */
export interface MeetingDay {
  /** `YYYY-MM-DD`. */
  day: string;
  timeZone: string;
}

/**
 * The meeting's day: its start, read as a calendar day on the meeting's own clock. `null` when the
 * start or the zone can't be read — and then every seat is offered (the rule below names no day to
 * test against, and hiding a seat would make a true fact unrecordable through the UI).
 */
export function meetingDayOf(
  meeting: { starts_at?: string | null; time_zone?: string | null } | null | undefined,
): MeetingDay | null {
  const zone = (meeting?.time_zone ?? '').trim();
  if (zone.length === 0 || !isUsableTimeZone(zone)) return null;
  const fields = instantToZonedFields(meeting?.starts_at ?? null, zone);
  return fields === null ? null : { day: fields.date, timeZone: zone };
}

function calendarDay(value: string | null | undefined): string | null {
  const day = (value ?? '').trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/**
 * ⚠ THE ONE RULE for which seats a meeting's pages offer — "served on the meeting's day" (CBG-FIX41,
 * the orchestrator's ruling 2026-10-09, read at 23:57 PDT). A seat is offered unless it had left the
 * board before that day:
 *
 *   * **Serving now** — offered, with NO START BOUND. A seat's `created_at` is only when someone typed
 *     it (a board enrolling in October would lose every September seat) and `term_start_on` is only
 *     its current term, so neither says when the person started serving.
 *   * **Removed** — a seat this module watched leaves on the day it was removed (`removed_at`), on
 *     the meeting's own clock: offered when that day is on or after the meeting's day. Its recorded
 *     term end (`term_end_on`, which the backend also serves as `service_history.served_to`) is the
 *     term's plan, never the day the person left — so a director removed mid-term is not offered for
 *     a meeting after the removal, and a holdover whose term had ended is offered for a meeting they
 *     served at.
 *   * **Entered as history** (`imported`, a former seat) — the organization's own account decides:
 *     offered when its recorded `served_to` is on or after the meeting's day (with none recorded,
 *     the day it was entered), and not when its recorded `served_from` is after it.
 *
 * The same rule serves a meeting today or ahead: it offers exactly the serving seats, plus anyone
 * removed that same day. The backend accepts any seat of the board for these acts ("Deliberately not
 * gated on `is_active`"), so this is a narrowing of what the page OFFERS, never of what it shows: a
 * seat already named by a recorded fact keeps its name, resolved from the whole roster.
 */
export function servedOnMeetingDay(seat: OrgBoardMemberOut, on: MeetingDay | null): boolean {
  if (on === null || seat.status === 'active') return true;
  const removed = instantToZonedFields(seat.removed_at ?? null, on.timeZone);
  const removedOn = removed === null ? null : removed.date;
  if (!isHistoricalSeat(seat)) return removedOn === null || removedOn >= on.day;
  const history = seat.service_history ?? null;
  const from = calendarDay(history?.served_from ?? seat.term_start_on);
  if (from !== null && from > on.day) return false;
  const end = calendarDay(history?.served_to ?? seat.term_end_on) ?? removedOn;
  return end === null || end >= on.day;
}

const NONE_NAMED: ReadonlySet<string> = new Set();

/**
 * The seats a meeting's pickers and attendance offer: those who served on its day (the rule above),
 * in roster order — and any seat a fact already recorded for this meeting names (`named`), so
 * correcting that fact never drops its seat from the picker.
 */
export function seatsServingOn(
  roster: readonly OrgBoardMemberOut[],
  on: MeetingDay | null,
  named: ReadonlySet<string> = NONE_NAMED,
): OrgBoardMemberOut[] {
  return roster.filter((seat) => named.has(seat.id) || servedOnMeetingDay(seat, on));
}

/** Every seat the motions' recorded facts name: movers, seconders, casts and recusals. */
export function motionNamedSeatIds(motions: readonly BoardMotionOut[]): Set<string> {
  const ids = new Set<string>();
  for (const motion of motions) {
    for (const id of [motion.moved_by_board_member_id, motion.seconded_by_board_member_id]) {
      if (typeof id === 'string' && id.length > 0) ids.add(id);
    }
    for (const row of [...extractCasts(motion), ...extractRecusals(motion)]) {
      if (typeof row?.board_member_id === 'string') ids.add(row.board_member_id);
    }
  }
  return ids;
}

/**
 * The seats an officer may be offered when recording what happened in the room.
 *
 * ⚠ **ROLE ONLY — NOT ACTIVE, and the two are different questions.** A director removed in the
 * autumn was in the room in the spring, and their vote is part of that meeting's record forever.
 * Which removed seats a meeting offers is the meeting-day rule's ({@link servedOnMeetingDay}), applied
 * by the panels to the WHOLE roster (`include_removed`) before this filter.
 *
 * The backend states this in `board_minutes_service._require_seat`, whose docstring on
 * `origin/development` reads "Deliberately not gated on ``is_active``: a director removed in the
 * autumn was in the room in the spring…". A dedicated regression
 * (`test_a_removed_directors_vote_is_still_recordable`, which exists to stop the gate being "tidied"
 * into membership of `counted_seat_ids`) **lands with backend PR #1711** — it is not on
 * `origin/development` yet, so cite the docstring, which is.
 *
 * Filtering removed seats out here would make a true fact unrecordable through the UI while the API
 * accepts it.
 *
 * The quorum DENOMINATOR additionally requires active — that is the same test's point: the removed
 * seat's vote stands AND it is out of `seats_counted`, because "did this seat vote" and "is this a
 * seat of the board today" are not one question. Neither figure is computed here in any case.
 *
 * Active-only belongs on surfaces about the PRESENT — RC2's RSVP path requires an active seat because
 * an RSVP is about a meeting still ahead. Everything this module offers is about what already
 * happened, so nothing here filters on status: the meeting's DAY does ({@link seatsServingOn}).
 *
 * The DIRECTOR filter stays, and stays positive (CBG-YR3-ROLES' G4, D-CBG-21): someone on the roster
 * who isn't a director has no vote, the backend refuses a cast for one, and offering the field would
 * produce a refusal the officer cannot act on. It reads `is_director` through the one predicate
 * (`officer-roles.ts`), never a negative test on the legacy word, which would admit any word a later
 * build adds.
 */
export function votingSeats(roster: readonly OrgBoardMemberOut[]): OrgBoardMemberOut[] {
  return roster.filter((seat) => isDirector(seat));
}

/**
 * Serving people who aren't directors: the vote dialog names them once, like a recusal, so the
 * officer isn't left wondering why they have no field.
 */
export function nonDirectorSeats(roster: readonly OrgBoardMemberOut[]): OrgBoardMemberOut[] {
  return servingNonDirectors(roster);
}

/**
 * A seat's name for a picker on a what-happened surface, carrying its removal state.
 *
 * A removed seat is OFFERED here (see {@link votingSeats}), so the label has to say so — otherwise an
 * officer picking from a list of names has no way to tell a current director from one who left, on a
 * screen where both are legitimately selectable.
 */
export function seatPickerLabel(seat: OrgBoardMemberOut): string {
  return seat.status === 'removed' ? `${seat.display_name} — no longer serving` : seat.display_name;
}
